import { useEffect, useState, useCallback } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Brain, TrendUp, TrendDown, Equals, SpinnerGap } from "@phosphor-icons/react";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

function formatINR(num) {
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num.toLocaleString("en-IN")}`;
}

function formatINRFull(num) {
  return `₹${Number(num).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const COLORS = ["#00E5FF", "#10B981", "#F59E0B", "#A78BFA", "#EF4444", "#EC4899", "#6366F1", "#14B8A6", "#F97316", "#8B5CF6"];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 p-3 text-xs" style={{ background: "#1A1A1A" }}>
      <p className="text-[#A1A1AA] mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-[#A1A1AA] capitalize">{p.name || p.dataKey.replace(/_/g, " ")}:</span>
          <span className="text-white font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {formatINRFull(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsPage() {
  const [weeklyData, setWeeklyData] = useState([]);
  const [forecastData, setForecastData] = useState({ historical: [], forecast: [], trend: "", projected_30d_total: 0 });
  const [performers, setPerformers] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [weeklyRes, forecastRes, perfRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/weekly-comparison`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/analytics/forecast`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/analytics/top-performers`, { headers: getAuthHeaders() }),
      ]);
      setWeeklyData(weeklyRes.data.weeks || []);
      setForecastData(forecastRes.data);
      setPerformers(perfRes.data.performers || []);
    } catch (err) {
      logger.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const res = await axios.post(`${API}/admin/analytics/ai-insights`, {}, { headers: getAuthHeaders() });
      setAiInsights(res.data);
    } catch (err) {
      setAiInsights({ insights: "Failed to fetch AI insights. Check LLM key configuration." });
    } finally {
      setLoadingAI(false);
    }
  };

  // Merge historical + forecast for the area chart
  const combinedChartData = [
    ...forecastData.historical.map((d) => ({
      ...d,
      displayDate: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    })),
    ...forecastData.forecast.map((d) => ({
      ...d,
      forecast_profit: d.total_profit,
      forecast_pool: d.safety_pool,
      total_profit: null,
      safety_pool: null,
      displayDate: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    })),
  ];

  // Add the bridge point so dotted line connects from last historical
  if (forecastData.historical.length > 0 && forecastData.forecast.length > 0) {
    const lastHist = forecastData.historical[forecastData.historical.length - 1];
    const bridgeIdx = forecastData.historical.length;
    if (combinedChartData[bridgeIdx]) {
      combinedChartData[bridgeIdx].forecast_profit = combinedChartData[bridgeIdx].forecast_profit;
      combinedChartData[bridgeIdx].forecast_pool = combinedChartData[bridgeIdx].forecast_pool;
    }
    // Also set forecast start on the last historical point
    combinedChartData[bridgeIdx - 1] = {
      ...combinedChartData[bridgeIdx - 1],
      forecast_profit: lastHist.total_profit,
      forecast_pool: lastHist.safety_pool,
    };
  }

  const TrendIcon = forecastData.trend === "growing" ? TrendUp : forecastData.trend === "declining" ? TrendDown : Equals;
  const trendColor = forecastData.trend === "growing" ? "#10B981" : forecastData.trend === "declining" ? "#EF4444" : "#F59E0B";

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* AI Insights Panel */}
      <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="ai-insights-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(0, 229, 255, 0.1)" }}>
              <Brain size={20} weight="duotone" className="text-[#00E5FF]" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                AI-Powered Insights
              </h3>
              <p className="text-[10px] text-[#A1A1AA]">GPT-5.2 analysis of your financial data</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10">
              <TrendIcon size={14} style={{ color: trendColor }} weight="bold" />
              <span className="text-[10px] font-bold capitalize" style={{ color: trendColor }}>{forecastData.trend}</span>
            </div>
            <button
              data-testid="generate-ai-insights-btn"
              onClick={fetchAIInsights}
              disabled={loadingAI}
              className="px-3 py-1.5 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {loadingAI ? <SpinnerGap size={14} className="animate-spin" /> : <Brain size={14} weight="bold" />}
              {loadingAI ? "Analyzing..." : "Generate Insights"}
            </button>
          </div>
        </div>

        {aiInsights ? (
          <div className="p-4 rounded-md border border-white/5 bg-white/[0.02]" data-testid="ai-insights-content">
            <p className="text-sm text-[#E0E0E0] whitespace-pre-line leading-relaxed">{aiInsights.insights}</p>
            {aiInsights.data_summary && (
              <div className="flex gap-4 mt-3 pt-3 border-t border-white/5">
                <span className="text-[10px] text-[#A1A1AA]">7d avg: <strong className="text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINRFull(aiInsights.data_summary.avg_7d)}</strong></span>
                <span className="text-[10px] text-[#A1A1AA]">30d avg: <strong className="text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINRFull(aiInsights.data_summary.avg_30d)}</strong></span>
                <span className="text-[10px] text-[#A1A1AA]">Users: <strong className="text-white">{aiInsights.data_summary.active_users}</strong></span>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-md border border-dashed border-white/10 text-center">
            <p className="text-xs text-[#A1A1AA]">Click "Generate Insights" for AI analysis of growth trends, regional performance, and revenue forecast</p>
          </div>
        )}
      </div>

      {/* Row: Weekly Comparison + Top Performers */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Weekly Bar Chart */}
        <div className="xl:col-span-2 rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="weekly-comparison-chart">
          <h3 className="text-sm font-bold text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Week-over-Week Revenue
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mb-4">Comparing weekly total profit and joining fees</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={weeklyData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="joining_fees" name="Joining Fees" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="safety_split" name="Safety Split" fill="#00E5FF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performers Donut */}
        <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="top-performers-chart">
          <h3 className="text-sm font-bold text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Top Performers
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mb-3">By joining fee contributions</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={performers.slice(0, 6)}
                dataKey="total_fees"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                strokeWidth={0}
              >
                {performers.slice(0, 6).map((_, idx) => (
                  <Cell key={`cell-${COLORS[idx]}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {performers.slice(0, 6).map((p, idx) => (
              <div key={p.name} className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[idx] }} />
                  <span className="text-white">{p.name}</span>
                  <span className="text-[#A1A1AA]">({p.region})</span>
                </div>
                <span className="text-[#A1A1AA]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatINR(p.total_fees)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Forecast Area Chart */}
      <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="forecast-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Revenue Trend + 30-Day Forecast
            </h3>
            <p className="text-[10px] text-[#A1A1AA] mt-0.5">
              Total Profit & Safety Pool with projected trajectory &middot; 30d projected: <strong className="text-[#00E5FF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINRFull(forecastData.projected_30d_total)}</strong>
            </p>
          </div>
          <div className="flex items-center gap-4 text-[10px]">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#00E5FF]" /><span className="text-[#A1A1AA]">Total Profit</span></span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10B981]" /><span className="text-[#A1A1AA]">Safety Pool</span></span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0 border-t-2 border-dashed border-[#F59E0B]" /><span className="text-[#A1A1AA]">Forecast</span></span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={combinedChartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <defs>
              <linearGradient id="profitGradA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="poolGradA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="displayDate" tick={{ fontSize: 9, fill: "#A1A1AA" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10, fill: "#A1A1AA" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v)} />
            <Tooltip content={<ChartTooltip />} />
            {/* Historical solid lines */}
            <Area type="monotone" dataKey="total_profit" name="Total Profit" stroke="#00E5FF" strokeWidth={2} fill="url(#profitGradA)" connectNulls={false} />
            <Area type="monotone" dataKey="safety_pool" name="Safety Pool" stroke="#10B981" strokeWidth={1.5} fill="url(#poolGradA)" connectNulls={false} />
            {/* Forecast dotted lines */}
            <Area type="monotone" dataKey="forecast_profit" name="Forecast Profit" stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 4" fill="none" connectNulls={false} />
            <Area type="monotone" dataKey="forecast_pool" name="Forecast Pool" stroke="#F59E0B" strokeWidth={1} strokeDasharray="4 3" fill="none" connectNulls={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
